import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ExpertWorkspaceExpertRole,
  ExpertWorkspaceRecord,
} from '@libs/shared/api';
import { Navigation } from '@sprint-expert/app/core/navigation/navigation.types';
import { Observable, of, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(
    1
  );
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this.navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  getByWorkspaceUserRole(
    expertWorkspace: ExpertWorkspaceRecord,
    role: ExpertWorkspaceExpertRole
  ): Observable<Navigation> {
    return of(this.buildNavigation(expertWorkspace, role));
  }

  buildNavigation(
    expertWorkspace: ExpertWorkspaceRecord,
    role: ExpertWorkspaceExpertRole
  ): Navigation {
    switch (expertWorkspace.activeSubscriptionPlanType) {
      case 'Personal':
        if (role === ExpertWorkspaceExpertRole.Member) {
          return buildPersonalMemberNavigation(expertWorkspace.id);
        } else {
          return buildPersonalAdminNavigation(expertWorkspace.id);
        }
      case 'Team': {
        if (role === ExpertWorkspaceExpertRole.Member) {
          return buildTeamMemberNavigation(expertWorkspace.id);
        } else {
          return buildTeamAdminNavigation(expertWorkspace.id);
        }
      }
    }
  }
}

export function buildTeamAdminNavigation(
  expertWorkspaceId: string
): Navigation {
  return {
    vertical: [
      {
        id: 'team',
        title: '팀',
        type: 'group',
        icon: 'mat_outline:groups',
        children: [
          {
            id: 'expert-workspace.expert-workspace-member',
            title: '전체 회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/team/expert-workspace-member`,
          },
          {
            id: 'team.expert-workspace-expert',
            title: '소속 코치',
            type: 'basic',
            icon: 'mat_outline:fitness_center',
            link: `/expert-workspace/${expertWorkspaceId}/team/expert-workspace-expert`,
          },
          {
            id: 'team.expert-workspace-schedule',
            title: '코치 일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/team/expert-workspace-schedule`,
          },
        ],
      },
      {
        id: 'personal',
        title: '개인',
        type: 'group',
        icon: 'mat_outline:person',
        children: [
          {
            id: 'personal.expert-member',
            title: '회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member`,
          },
          {
            id: 'personal.expert-schedule',
            title: '일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/expert-schedule`,
          },
          {
            id: 'personal.expert-member-chat',
            title: '채팅',
            type: 'basic',
            icon: 'heroicons_outline:chat-bubble-left-right',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-chat`,
          },
        ],
      },
      {
        id: 'expert-workspace',
        title: '센터',
        type: 'group',
        icon: 'mat_outline:fitness_center',
        children: [
          {
            id: 'expert-workspace.dashboard',
            title: '대시보드',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/expert-workspace/${expertWorkspaceId}/dashboard`,
          },
          {
            id: 'expert-workspace.expert-member-pass-template',
            title: '수강권 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:ticket',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-pass-template`,
          },
          {
            id: 'expert-workspace.expert-member-survey-template',
            title: '상담 설문 템플릿',
            type: 'basic',
            icon: 'mat_outline:question_answer',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-survey-template`,
          },
          {
            id: 'expert-workspace.workout-template',
            title: '운동 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-document',
            link: `/expert-workspace/${expertWorkspaceId}/workout-template`,
          },
          {
            id: 'expert-workspace.expert-member-tag',
            title: '회원 태그',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-tag`,
          },
        ],
      },
      {
        id: 'settings',
        title: 'Settings',
        subtitle: '계정 관련 세팅',
        type: 'group',
        icon: 'mat_outline:settings',
        children: [
          {
            id: 'settings.settings',
            title: '설정',
            type: 'basic',
            icon: 'mat_outline:settings',
            link: `/expert-workspace/${expertWorkspaceId}/settings`,
          },
        ],
      },
    ],
    horizontal: [
      {
        id: 'team',
        title: '팀',
        type: 'group',
        icon: 'mat_outline:groups',
        children: [
          {
            id: 'team.expert-workspace-member',
            title: '전체 회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/team/expert-workspace-member`,
          },
          {
            id: 'team.expert-workspace-expert',
            title: '소속 코치',
            type: 'basic',
            icon: 'mat_outline:fitness_center',
            link: `/expert-workspace/${expertWorkspaceId}/team/expert-workspace-expert`,
          },
          {
            id: 'team.expert-workspace-schedule',
            title: '코치 일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/team/expert-workspace-schedule`,
          },
        ],
      },
      {
        id: 'personal',
        title: '개인',
        type: 'group',
        icon: 'mat_outline:person',
        children: [
          {
            id: 'personal.expert-member',
            title: '회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member`,
          },
          {
            id: 'personal.expert-schedule',
            title: '일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/expert-schedule`,
          },
          {
            id: 'personal.expert-member-chat',
            title: '채팅',
            type: 'basic',
            icon: 'heroicons_outline:chat-bubble-left-right',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-chat`,
          },
        ],
      },
      {
        id: 'workspace',
        title: '센터',
        type: 'group',
        icon: 'mat_outline:fitness_center',
        children: [
          {
            id: 'expert-workspace.dashboard',
            title: '대시보드',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/expert-workspace/${expertWorkspaceId}/dashboard`,
          },
          {
            id: 'expert-workspace.expert-member-pass-template',
            title: '수강권 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:ticket',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-pass-template`,
          },
          {
            id: 'expert-workspace.expert-member-survey-template',
            title: '상담 설문 템플릿',
            type: 'basic',
            icon: 'mat_outline:question_answer',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-survey-template`,
          },
          {
            id: 'expert-workspace.workout-template',
            title: '운동 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-document',
            link: `/expert-workspace/${expertWorkspaceId}/workout-template`,
          },
          {
            id: 'expert-workspace.expert-member-tag',
            title: '회원 태그',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-tag`,
          },
        ],
      },
      {
        id: 'settings',
        title: '설정',
        type: 'basic',
        icon: 'mat_outline:settings',
        link: `/expert-workspace/${expertWorkspaceId}/settings`,
      },
    ],
  };
}

export function buildTeamMemberNavigation(
  expertWorkspaceId: string
): Navigation {
  return {
    vertical: [
      {
        id: 'personal',
        title: '개인',
        type: 'group',
        icon: 'mat_outline:person',
        children: [
          {
            id: 'personal.expert-member',
            title: '회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member`,
          },
          {
            id: 'personal.expert-schedule',
            title: '일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/expert-schedule`,
          },
          {
            id: 'personal.expert-member-chat',
            title: '채팅',
            type: 'basic',
            icon: 'heroicons_outline:chat-bubble-left-right',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-chat`,
          },
        ],
      },
      {
        id: 'expert-workspace',
        title: '센터',
        type: 'group',
        icon: 'mat_outline:fitness_center',
        children: [
          {
            id: 'expert-workspace.dashboard',
            title: '대시보드',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/expert-workspace/${expertWorkspaceId}/dashboard`,
          },
          {
            id: 'expert-workspace.expert-member-pass-template',
            title: '수강권 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:ticket',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-pass-template`,
          },
          {
            id: 'expert-workspace.expert-member-survey-template',
            title: '상담 설문 템플릿',
            type: 'basic',
            icon: 'mat_outline:question_answer',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-survey-template`,
          },
          {
            id: 'expert-workspace.workout-template',
            title: '운동 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-document',
            link: `/expert-workspace/${expertWorkspaceId}/workout-template`,
          },
          {
            id: 'expert-workspace.expert-member-tag',
            title: '회원 태그',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-tag`,
          },
        ],
      },
      {
        id: 'settings',
        title: 'Settings',
        subtitle: '계정 관련 세팅',
        type: 'group',
        icon: 'mat_outline:settings',
        children: [
          {
            id: 'settings.settings',
            title: '설정',
            type: 'basic',
            icon: 'mat_outline:settings',
            link: `/expert-workspace/${expertWorkspaceId}/settings`,
          },
        ],
      },
    ],
    horizontal: [
      {
        id: 'personal',
        title: '개인',
        type: 'group',
        icon: 'mat_outline:person',
        children: [
          {
            id: 'personal.expert-member',
            title: '회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member`,
          },
          {
            id: 'personal.expert-schedule',
            title: '일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/expert-schedule`,
          },
          {
            id: 'personal.expert-member-chat',
            title: '채팅',
            type: 'basic',
            icon: 'heroicons_outline:chat-bubble-left-right',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-chat`,
          },
        ],
      },
      {
        id: 'workspace',
        title: '센터',
        type: 'group',
        icon: 'mat_outline:fitness_center',
        children: [
          {
            id: 'expert-workspace.dashboard',
            title: '대시보드',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/expert-workspace/${expertWorkspaceId}/dashboard`,
          },
          {
            id: 'expert-workspace.expert-member-pass-template',
            title: '수강권 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:ticket',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-pass-template`,
          },
          {
            id: 'expert-workspace.expert-member-survey-template',
            title: '상담 설문 템플릿',
            type: 'basic',
            icon: 'mat_outline:question_answer',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-survey-template`,
          },
          {
            id: 'expert-workspace.workout-template',
            title: '운동 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-document',
            link: `/expert-workspace/${expertWorkspaceId}/workout-template`,
          },
          {
            id: 'expert-workspace.expert-member-tag',
            title: '회원 태그',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-tag`,
          },
        ],
      },
      {
        id: 'settings',
        title: '설정',
        type: 'basic',
        icon: 'mat_outline:settings',
        link: `/expert-workspace/${expertWorkspaceId}/settings`,
      },
    ],
  };
}

export function buildPersonalAdminNavigation(
  expertWorkspaceId: string
): Navigation {
  return {
    vertical: [
      {
        id: 'personal',
        title: '개인',
        type: 'group',
        icon: 'mat_outline:person',
        children: [
          {
            id: 'personal.expert-member',
            title: '회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member`,
          },
          {
            id: 'personal.expert-schedule',
            title: '일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/expert-schedule`,
          },
          {
            id: 'personal.expert-member-chat',
            title: '채팅',
            type: 'basic',
            icon: 'heroicons_outline:chat-bubble-left-right',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-chat`,
          },
        ],
      },
      {
        id: 'expert-workspace',
        title: '센터',
        type: 'group',
        icon: 'mat_outline:fitness_center',
        children: [
          {
            id: 'expert-workspace.dashboard',
            title: '대시보드',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/expert-workspace/${expertWorkspaceId}/dashboard`,
          },
          {
            id: 'expert-workspace.expert-member-pass-template',
            title: '수강권 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:ticket',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-pass-template`,
          },
          {
            id: 'expert-workspace.expert-member-survey-template',
            title: '상담 설문 템플릿',
            type: 'basic',
            icon: 'mat_outline:question_answer',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-survey-template`,
          },
          {
            id: 'expert-workspace.workout-template',
            title: '운동 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-document',
            link: `/expert-workspace/${expertWorkspaceId}/workout-template`,
          },
          {
            id: 'expert-workspace.expert-member-tag',
            title: '회원 태그',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-tag`,
          },
        ],
      },
      {
        id: 'settings',
        title: 'Settings',
        subtitle: '계정 관련 세팅',
        type: 'group',
        icon: 'mat_outline:settings',
        children: [
          {
            id: 'settings.settings',
            title: '설정',
            type: 'basic',
            icon: 'mat_outline:settings',
            link: `/expert-workspace/${expertWorkspaceId}/settings`,
          },
        ],
      },
    ],
    horizontal: [
      {
        id: 'personal',
        title: '개인',
        type: 'group',
        icon: 'mat_outline:person',
        children: [
          {
            id: 'personal.expert-member',
            title: '회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member`,
          },
          {
            id: 'personal.expert-schedule',
            title: '일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/expert-schedule`,
          },
          {
            id: 'personal.expert-member-chat',
            title: '채팅',
            type: 'basic',
            icon: 'heroicons_outline:chat-bubble-left-right',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-chat`,
          },
        ],
      },
      {
        id: 'workspace',
        title: '센터',
        type: 'group',
        icon: 'mat_outline:fitness_center',
        children: [
          {
            id: 'expert-workspace.dashboard',
            title: '대시보드',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/expert-workspace/${expertWorkspaceId}/dashboard`,
          },
          {
            id: 'expert-workspace.expert-member-pass-template',
            title: '수강권 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:ticket',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-pass-template`,
          },
          {
            id: 'expert-workspace.expert-member-survey-template',
            title: '상담 설문 템플릿',
            type: 'basic',
            icon: 'mat_outline:question_answer',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-survey-template`,
          },
          {
            id: 'expert-workspace.workout-template',
            title: '운동 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-document',
            link: `/expert-workspace/${expertWorkspaceId}/workout-template`,
          },
          {
            id: 'expert-workspace.expert-member-tag',
            title: '회원 태그',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-tag`,
          },
        ],
      },
      {
        id: 'settings',
        title: '설정',
        type: 'basic',
        icon: 'mat_outline:settings',
        link: `/expert-workspace/${expertWorkspaceId}/settings`,
      },
    ],
  };
}

export function buildPersonalMemberNavigation(
  expertWorkspaceId: string
): Navigation {
  return {
    vertical: [
      {
        id: 'personal',
        title: '개인',
        type: 'group',
        icon: 'mat_outline:person',
        children: [
          {
            id: 'personal.expert-member',
            title: '회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member`,
          },
          {
            id: 'personal.expert-schedule',
            title: '일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/expert-schedule`,
          },
          {
            id: 'personal.expert-member-chat',
            title: '채팅',
            type: 'basic',
            icon: 'heroicons_outline:chat-bubble-left-right',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-chat`,
          },
        ],
      },
      {
        id: 'expert-workspace',
        title: '센터',
        type: 'group',
        icon: 'mat_outline:fitness_center',
        children: [
          {
            id: 'expert-workspace.dashboard',
            title: '대시보드',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/expert-workspace/${expertWorkspaceId}/dashboard`,
          },
          {
            id: 'expert-workspace.expert-member-pass-template',
            title: '수강권 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:ticket',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-pass-template`,
          },
          {
            id: 'expert-workspace.expert-member-survey-template',
            title: '상담 설문 템플릿',
            type: 'basic',
            icon: 'mat_outline:question_answer',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-survey-template`,
          },
          {
            id: 'expert-workspace.workout-template',
            title: '운동 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-document',
            link: `/expert-workspace/${expertWorkspaceId}/workout-template`,
          },
          {
            id: 'expert-workspace.expert-member-tag',
            title: '회원 태그',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-tag`,
          },
        ],
      },
      {
        id: 'settings',
        title: 'Settings',
        subtitle: '계정 관련 세팅',
        type: 'group',
        icon: 'mat_outline:settings',
        children: [
          {
            id: 'settings.settings',
            title: '설정',
            type: 'basic',
            icon: 'mat_outline:settings',
            link: `/expert-workspace/${expertWorkspaceId}/settings`,
          },
        ],
      },
    ],
    horizontal: [
      {
        id: 'personal',
        title: '개인',
        type: 'group',
        icon: 'mat_outline:person',
        children: [
          {
            id: 'personal.expert-member',
            title: '회원',
            type: 'basic',
            icon: 'heroicons_solid:user-group',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member`,
          },
          {
            id: 'personal.expert-schedule',
            title: '일정',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: `/expert-workspace/${expertWorkspaceId}/expert-schedule`,
          },
          {
            id: 'personal.expert-member-chat',
            title: '채팅',
            type: 'basic',
            icon: 'heroicons_outline:chat-bubble-left-right',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-chat`,
          },
        ],
      },
      {
        id: 'workspace',
        title: '센터',
        type: 'group',
        icon: 'mat_outline:fitness_center',
        children: [
          {
            id: 'expert-workspace.dashboard',
            title: '대시보드',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/expert-workspace/${expertWorkspaceId}/dashboard`,
          },
          {
            id: 'expert-workspace.expert-member-pass-template',
            title: '수강권 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:ticket',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-pass-template`,
          },
          {
            id: 'expert-workspace.expert-member-survey-template',
            title: '상담 설문 템플릿',
            type: 'basic',
            icon: 'mat_outline:question_answer',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-survey-template`,
          },
          {
            id: 'expert-workspace.workout-template',
            title: '운동 템플릿',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-document',
            link: `/expert-workspace/${expertWorkspaceId}/workout-template`,
          },
          {
            id: 'expert-workspace.expert-member-tag',
            title: '회원 태그',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: `/expert-workspace/${expertWorkspaceId}/expert-member-tag`,
          },
        ],
      },
      {
        id: 'settings',
        title: '설정',
        type: 'basic',
        icon: 'mat_outline:settings',
        link: `/expert-workspace/${expertWorkspaceId}/settings`,
      },
    ],
  };
}
